import './../../styles/pages/member-voting.scss';
import React, { useEffect, useState } from 'react';
import {
  getElections,
  getElectionResults
} from '../../core/services/voting';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  LinearProgress,
  Container,
  Card,
  CardContent,
  Stack,
  Chip
} from '@mui/material';
import { getUserFromSessionStorage } from '../../core/services/auth';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { electionManagementAuth } from '../../core/services/elections';

const csvConfig = mkConfig({
  useKeysAsHeaders: true,
  showColumnHeaders: false
});

const ElectionResults = () => {
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    (async () => {
      const code = await electionManagementAuth();

      if (window !== undefined) {
        if (window.prompt('Are you sure you want to proceed?') === code) {
          setAuthed(true);
        } else {
          setAuthed(false);
        }
      }
    })();
  }, []);

  const user = getUserFromSessionStorage();
  const [elections, setElections] = useState([]);
  const [selectedElection, setSelectedElection] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (authed) {
      const fetchElections = async () => {
        const res = await getElections();
        setElections(res.elections);
      };
      fetchElections();
    }
  }, [authed]);

  const handleElectionChange = event => {
    setSelectedElection(event.target.value);
  };

  const handleGenerateResults = async () => {
    const data = [];

    setLoading(true);
    const res = await getElectionResults(selectedElection);
    setResults(res.results);
    setLoading(false);

    if (res.results) {
      const d = res.results;

      data.push({
        Candidate: 'TOTAL VOTES',
        Votes: d.totalVotes
      });

      data.push({
        Candidate: 'MEMBERS VOTED',
        Votes: d.membersVoted
      });

      d.positions.forEach(position => {
        position.candidates.forEach(candidate => {
          data.push({
            Candidate: position.name + ' - ' + candidate.name,
            Votes: candidate.votes
          });
        });
      });

      data.push({
        Candidate: '',
        Votes: ''
      });

      data.push({
        Candidate: d.poll.question,
        Votes: ''
      });

      d.poll.answers.forEach(answer => {
        data.push({
          Candidate: answer.answer,
          Votes: answer.count
        });
      });

      data.push({
        Candidate: 'TOTAL',
        Votes: d.poll.total
      });
    }

    const csv = generateCsv(csvConfig)(data);
    download(csvConfig)(csv);
  };

  if (user.role !== 'admin') {
    return <Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6" color="error">Access Denied</Typography></Box>;
  }

  // Helper to calculate percentage
  const calculatePercentage = (votes, total) => {
    if (!total || total === 0) return 0;
    return (votes / total) * 100;
  };

  return authed ? (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
         <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: '#1a1a1a' }}>
           Election Results
         </Typography>
         <Typography color="text.secondary">
            View real-time analytic results for cooperative elections.
         </Typography>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
         <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
               <FormControl variant="outlined" fullWidth size="small" sx={{ bgcolor: 'white' }}>
                  <InputLabel id="select-election-label">Select Election</InputLabel>
                  <Select
                    labelId="select-election-label"
                    value={selectedElection}
                    onChange={handleElectionChange}
                    label="Select Election"
                  >
                    {elections.map(election => (
                      <MenuItem key={election.id} value={election.id}>
                        {election.title}
                      </MenuItem>
                    ))}
                  </Select>
               </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
               <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGenerateResults}
                  disabled={!selectedElection || loading}
                  sx={{ 
                     minHeight: 56, 
                     bgcolor: '#35b258', 
                     fontWeight: 600,
                     lineHeight: 1.2,
                     px: 3,
                     '&:hover': { bgcolor: '#2e964b' }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Generate & Download CSV'}
                </Button>
            </Grid>
         </Grid>
      </Paper>

      {results && (
        <Box sx={{ animation: 'fadeIn 0.5s ease-in-out' }}>
           {/* Summary Cards */}
           <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                 <Card elevation={0} sx={{ bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 3 }}>
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                       <Typography variant="h3" sx={{ fontWeight: 800, color: '#2e7d32' }}>
                          {results.totalVotes}
                       </Typography>
                       <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1b5e20', letterSpacing: 1, mt: 1 }}>
                          TOTAL VOTES CAST
                       </Typography>
                    </CardContent>
                 </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                 <Card elevation={0} sx={{ bgcolor: '#e3f2fd', border: '1px solid #bbdefb', borderRadius: 3 }}>
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                       <Typography variant="h3" sx={{ fontWeight: 800, color: '#1565c0' }}>
                          {results.membersVoted}
                       </Typography>
                       <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0d47a1', letterSpacing: 1, mt: 1 }}>
                          MEMBERS VOTED
                       </Typography>
                    </CardContent>
                 </Card>
              </Grid>
           </Grid>

           {/* Results By Position */}
           {results.positions.map(position => {
              // Calculate total votes for this specific position to determine percentages locally if needed,
              // or use the candidate votes. Here we'll usually visualize relative to the max votes or 100%.
              // Simple percentage based on total votes cast in election might be misleading if users can pick multiple.
              // We'll calculate percentage based on the sum of votes for that position for better relative visualization.
              const totalPositionVotes = position.candidates.reduce((sum, c) => sum + parseInt(c.votes), 0);
              // Find max votes to highlight winner
              const maxVotes = Math.max(...position.candidates.map(c => parseInt(c.votes)));

              return (
                 <Paper key={position.id} elevation={0} sx={{ p: 4, mb: 4, borderRadius: 3, border: '1px solid #e0e0e0' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#35b258' }}>
                       {position.name}
                    </Typography>
                    <Stack spacing={3}>
                       {position.candidates.map(candidate => {
                          const votes = parseInt(candidate.votes);
                          const isWinner = votes === maxVotes && votes > 0;
                          const percentage = totalPositionVotes > 0 ? (votes / totalPositionVotes) * 100 : 0;
                          
                          return (
                             <Box key={candidate.id}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                         {candidate.name}
                                      </Typography>
                                      {isWinner && (
                                         <Chip label="Lead" size="small" color="success" sx={{ height: 20, fontSize: '0.625rem', fontWeight: 700 }} />
                                      )}
                                   </Box>
                                   <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                      {candidate.votes} Votes
                                   </Typography>
                                </Box>
                                <LinearProgress 
                                   variant="determinate" 
                                   value={percentage} 
                                   sx={{ 
                                      height: 10, 
                                      borderRadius: 5,
                                      bgcolor: '#f1f1f1',
                                      '& .MuiLinearProgress-bar': {
                                         bgcolor: isWinner ? '#35b258' : '#bdbdbd',
                                         borderRadius: 5
                                      }
                                   }} 
                                />
                             </Box>
                          );
                       })}
                    </Stack>
                 </Paper>
              );
           })}

           {/* Poll Results */}
           {results?.poll?.question && (
             <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 3, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
                   Poll: {results.poll.question}
                </Typography>
                <Stack spacing={3}>
                   {results.poll.answers.map(answer => {
                      const percentage = calculatePercentage(answer.count, results.poll.total);
                      return (
                         <Box key={answer.answer}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                               <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                  {answer.answer}
                               </Typography>
                               <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  {answer.count} ({percentage.toFixed(1)}%)
                               </Typography>
                            </Box>
                            <LinearProgress 
                               variant="determinate" 
                               value={percentage} 
                               sx={{ 
                                  height: 10, 
                                  borderRadius: 5,
                                  bgcolor: '#f1f1f1',
                                  '& .MuiLinearProgress-bar': {
                                     bgcolor: '#1976d2',
                                     borderRadius: 5
                                  }
                               }} 
                            />
                         </Box>
                      );
                   })}
                   <Box sx={{ pt: 2, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                         Total Poll Responses: {results.poll.total}
                      </Typography>
                   </Box>
                </Stack>
             </Paper>
           )}
        </Box>
      )}
    </Container>
  ) : null;
};

export default ElectionResults;
